import { useState } from 'react';
import axios from 'axios';
import ApiEndpoints from '../../const/ApiEndpoints';
import useProject from '../Project/useProject';
import useUDFManager from '../UDFManager/useUDFManager';
import useDataIngestion from '../DM/useDataIngestion';
import SwalToast from '../../components/Common/SwalToast';

const ME_BASE_URL = ApiEndpoints.MODULE_BASE_URL.ME;

export default function useModelUpload() {
  const {
    datasetList,
    project,
    fetchDatasetList,
    fetchDatasetListWithModelId,
    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,
    fetchGroups,
    fetchUserRecipients,
    fetchUserProjects,
    fetchProjects,
  } = useProject();
  const { fetchAllUDFs } = useUDFManager();
  const { cloneArtifacts } = useDataIngestion();
  const totalPageCount = 'x-total-count';
  // Saved Models
  const [models, setModels] = useState([]);

  // deploy model
  const [deployedTableId, setdeployedTableId] = useState(null);

  const [parameters, setParameters] = useState('');

  const [modelListWithPagination, setModelListWithPagination] = useState([]);
  const [
    modelListPageCountWithPagination,
    setModelListPageCountWithPagination,
  ] = useState(0);
  const [totalModelListWithPagination, setTotalModelListWithPagination] =
    useState(0);

  const [modelListWithoutPagination, setModelListWithoutPagination] = useState(
    []
  );
  // model summary stats
  const [modelSummaryStats, setModelSummaryStats] = useState({});
  // model related jobs
  const [modelRelatedJobs, setModelRelatedJobs] = useState([]);
  const [totalModelRelatedJobs, setTotalModelRelatedJobs] = useState(0);
  const [modelRelatedJobsPageCount, setModelRelatedJobsPageCount] = useState(0);
  // model related pipelines
  const [modelRelatedPipelines, setModelRelatedPipelines] = useState([]);
  const [totalModelRelatedPipelines, setTotalModelRelatedPipelines] =
    useState(0);
  const [modelRelatedPipelinesPageCount, setModelRelatedPipelinesPageCount] =
    useState(0);
  const [modelDocuments, setModelDocuments] = useState([]);
  const [totalModelDocuments, setTotalModelDocuments] = useState(0);
  const [modelDocumentsPageCount, setModelDocumentsPageCount] = useState(0);
  const [modelRelatedUdfs, setModelRelatedUdfs] = useState([]);
  const [totalModelRelatedUdfs, setTotalModelRelatedUdfs] = useState(0);
  const [modelRelatedUdfsPageCount, setModelRelatedUdfsPageCount] = useState(0);
  // target variables
  const [targetColumns, setTargetColumns] = useState([]);
  const [datasetColumnDetails, setDatasetColumnDetails] = useState([]);
  const TYPE_ALL = 'Type (All)';

  const [modelLinkedProjects, setModelLinkedProjects] = useState([]);
  const [totalModelLinkedProjects, setTotalModelLinkedProjects] = useState(0);
  const [modelLinkedProjectsPageCount, setModelLinkedProjectsPageCount] =
    useState(0);

  // dataset rows
  // const [datasetRowsWithPagination, setDatasetRowsWithPagination] = useState(
  //   []
  // );
  // const [datasetRowsPageCount, setDatasetRowPageCount] = useState(0);
  // const [totalDatasetRows, setTotalDatasetRows] = useState(0);
  // const [isLoading, setIsLoading] = useState(false);
  /**
   * @function deploySavedModel
   * @description API call for deploying saved modal
   * @param {object} body - data for deploying saved modal
   * @returns {Promise} AxiosPromise
   * @example deploySavedModel(body);
   */
  const deploySavedModel = (body) =>
    axios.post(ME_BASE_URL + ApiEndpoints.ME.ME_DEPLOY_MODEL, body).then(
      (res) => {
        setdeployedTableId(res.data.response);
        return res;
      },
      (error) => {
        throw error;
      }
    );
  /**
   * @function uploadPklFile
   * @description API call for uploading pkl file
   * @param {object} body - data for uploading pkl file
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @param {function} errorCallback - Callback function to execute
   *  when any error happens during the calling of API
   * @returns {Promise} AxiosPromise
   * @example uploadPklFile(body, successCallback, errorCallback);
   */
  const uploadPklFile = (body, successCallback, errorCallback = () => {}) => {
    const urlNonTimeSeries = ME_BASE_URL + ApiEndpoints.ME.ME_UPLOAD_PKL;
    return axios
      .post(urlNonTimeSeries, body, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(
        () => {
          successCallback();
        },
        (error) => {
          errorCallback(error);
        }
      );
  };
  const uploadSas = (body) =>
    axios.post(ME_BASE_URL + ApiEndpoints.ME.ME_UPLOAD_SAS, body).then(
      (res) => {
        setParameters(res.data);
        return res;
      },
      (error) => {
        throw error;
      }
    );

  /**
   * @function fetchParameters
   * @description API call for fetching parameters
   * @param {object} body - data for fetching parameters
   * @param {function} successCallback - Callback function to execute upon completion of API call
   * @param {function} errorCallback - Callback function to execute
   * when any error happens during the calling of API
   * @returns {Promise} AxiosPromise
   * @example fetchParameters(body, successCallback, errorCallback);
   */
  const fetchParameters = (body) =>
    axios.post(ME_BASE_URL + ApiEndpoints.ME.ME_GET_PARAMETERS, body).then(
      (res) => {
        setParameters(res.data);
        return res;
      },
      (error) => {
        SwalToast({
          icon: 'error',
          title: 'Failed to fetch model parameters.',
        });
        throw error;
      }
    );
  /**
   * @function fetchSavedModelList
   * @description API call for fetching saved model list
   * @param {object} data - data for fetching saved model list
   * @param {boolean} loader - show loader
   * @param {string} field - fields for fetching saved model list
   * @returns {Promise} AxiosPromise
   * @example fetchSavedModelList(data);
   */
  const fetchSavedModelList = (data, loader = true, field = '') => {
    return axios
      .get(ME_BASE_URL + ApiEndpoints.ME.ME_GET_SAVED_MODEL_LIST, {
        params: { ...data, ...(field ? { fields: field } : {}) },
        // @ts-ignore
        loader,
      })
      .then(
        (res) => {
          const dt = res.data.filter((e) => {
            if (e.external_pkl) {
              return e;
            }
            return null;
          });
          setModels(dt);
          return res;
        },
        (error) => {
          throw error;
        }
      );
  };

  /**
   * @function fetchAllModelList
   * @description API call for fetching saved model list
   * @param {object} data - data for fetching saved model list
   * @returns {Promise} AxiosPromise
   * @example fetchAllModelList(data);
   */
  const fetchAllModelListWithPagination = (
    projectId,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    modelLanguage = '',
    modelType = '',
    modelStatus = '',
    field = '',
    sortField = '',
    sortOrder = ''
  ) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.ME_GET_SAVED_MODEL_LIST, {
        params: {
          page_index: pageIndex + 1,
          ...(projectId ? { project_id: projectId } : {}),
          ...(pageSize ? { page_size: pageSize } : {}),
          ...(searchText !== '' ? { search_term: searchText } : {}),
          ...(modelLanguage !== TYPE_ALL ? { language: modelLanguage } : {}),
          ...(modelType !== TYPE_ALL ? { model_type: modelType } : {}),
          ...(modelStatus !== TYPE_ALL ? { archive: modelStatus } : {}),
          ...(sortField ? { sort_field: sortField } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
          ...(field ? { fields: field } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setModelListWithPagination(res.data);
            setTotalModelListWithPagination(res.headers[totalPageCount]);
            setModelListPageCountWithPagination(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setModelListWithPagination([]);
            setModelListPageCountWithPagination(0);
            setTotalModelListWithPagination(0);
          }
          return res;
        },
        (error) => {
          setModelListWithPagination([]);
          setModelListPageCountWithPagination(0);
          setTotalModelListWithPagination(0);
          throw error;
        }
      );

  const fetchAllModelListWithoutPagination = (projectId, field) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.ME_GET_SAVED_MODEL_LIST, {
        params: {
          project_id: projectId,
          ...(field ? { fields: field } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setModelListWithoutPagination(res.data);
          } else {
            setModelListWithoutPagination([]);
          }
          return res;
        },
        (error) => {
          setModelListWithoutPagination([]);
          throw error;
        }
      );
  /**
   * @function fetchTargetColumns
   * @description API call for fetching target columns
   * @param {object} body- data for fetching target columns
   * @returns {Promise} AxiosPromise
   * @example fetchTargetColumns(body);
   */
  const fetchTargetColumns = (body) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.DM + ApiEndpoints.DM.GET_TARGET_LIST}`,
        body
      )
      .then(
        (res) => {
          setTargetColumns(res.data.valid_targets);
        },
        () => {
          setTargetColumns([]);
        }
      );
  /**
   * @function fetchDatasetColumnsDetails
   * @description API call for fetching dataset columns details
   * @param {object} body- data for fetching dataset columns details
   * @returns {Promise} AxiosPromise
   * @example fetchDatasetColumnsDetails(body);
   */
  const fetchDatasetColumnsDetails = (body) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.DM +
          ApiEndpoints.DM.DATASET_COLUMN_DETAILS
        }`,
        body
      )
      .then(
        (res) => {
          setDatasetColumnDetails(res?.data?.columns);
        },
        () => {
          setDatasetColumnDetails([]);
        }
      );
  /**
   * @function  renameModelDisplayName
   * @description API call for renaming dataset display name.
   * @param {object} payload - payload data
   * @returns {Promise} AxiosPromise
   * @example  renameModelDisplayName(payload)
   */
  const renameModelDisplayName = (payload) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME +
          ApiEndpoints.ME.RENAME_MODEL_DISPLAY_NAME
        }`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  const deleteModel = (payload) =>
    axios
      .delete(
        `${ApiEndpoints.MODULE_BASE_URL.ME}${ApiEndpoints.ME.DELETE_MODEL}`,
        {
          data: {
            model_id: payload.model_id,
          },
        }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function  fetchModelSummaryStats
   * @description API call for fetching model summary statistics.
   * @param {number} projectId - active project id
   * @returns {Promise} AxiosPromise
   * @example  fetchModelSummaryStats(payload)
   */
  const fetchModelSummaryStats = (projectId) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_MODEL_SUMMARY_STATS, {
        params: {
          ...(projectId ? { project_id: projectId } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setModelSummaryStats(res.data);
          } else {
            setModelSummaryStats([]);
          }
          return res;
        },
        (error) => {
          setModelSummaryStats([]);
          throw error;
        }
      );
  /**
   * @function  fetchModelRelatedJobs
   * @description API call for fetching model related jobs.
   * @param {number} modelId - selected model id
   * @param {number} pageIndex - current page index
   * @param {number} pageSize - current page size
   * @param {string} searchText - search value
   * @returns {Promise} AxiosPromise
   * @example  fetchModelRelatedJobs(payload)
   */
  const fetchModelRelatedJobs = (
    projectId = null,
    modelId,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    sortField,
    sortOrder
  ) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_RELATEDS_JOBS, {
        params: {
          ...(projectId ? { project_id: projectId } : {}),
          model_id: modelId,
          ...(pageSize ? { page_size: pageSize } : {}),
          page_index: pageIndex + 1,
          ...(searchText !== '' ? { search_term: searchText } : {}),
          ...(sortField ? { sort_field: sortField } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setModelRelatedJobs(res.data);
            setTotalModelRelatedJobs(res.headers[totalPageCount]);
            setModelRelatedJobsPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setModelRelatedJobs([]);
            setTotalModelRelatedJobs(0);
            setModelRelatedJobsPageCount(0);
          }
          return res;
        },
        (error) => {
          setModelRelatedJobs([]);
          setTotalModelRelatedJobs(0);
          setModelRelatedJobsPageCount(0);
          throw error;
        }
      );
  /**
   * @function  fetchModelRelatedPipelines
   * @description API call for fetching model related jobs.
   * @param {number} modelId - selected model id
   * @param {number} pageIndex - current page index
   * @param {number} pageSize - current page size
   * @param {string} searchText - search value
   * @returns {Promise} AxiosPromise
   * @example  fetchModelRelatedPipelines(payload)
   */
  const fetchModelRelatedPipelines = (
    projectId = null,
    modelId,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    sortField,
    sortOrder
  ) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_RELATEDS_PIPELINES, {
        params: {
          ...(projectId ? { project_id: projectId } : {}),
          model_id: modelId,
          ...(pageSize ? { page_size: pageSize } : {}),
          page_index: pageIndex + 1,
          ...(searchText !== '' ? { search_term: searchText } : {}),
          ...(sortField ? { sort_field: sortField } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setModelRelatedPipelines(res.data);
            setTotalModelRelatedPipelines(res.headers[totalPageCount]);
            setModelRelatedPipelinesPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setModelRelatedPipelines([]);
            setTotalModelRelatedPipelines(0);
            setModelRelatedPipelinesPageCount(0);
          }
          return res;
        },
        (error) => {
          setModelRelatedPipelines([]);
          setTotalModelRelatedPipelines(0);
          setModelRelatedPipelinesPageCount(0);
          throw error;
        }
      );
  /**
   * @function  fetchModelDetails
   * @description API call for fetching model details.
   * @param {number} modelId - selected model id
   * @returns {Promise} AxiosPromise
   * @example  fetchModelDetails(modelId)
   */
  const fetchModelDetails = (modelId) =>
    axios.get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_MODEL_DETAILS, {
      params: {
        model_id: modelId,
      },
    });

  /**
   * @function  updateModelDetails
   * @description API call for updating model details.
   * @param {object} payload - payload data
   * @returns {Promise} AxiosPromise
   * @example  updateModelDetails(payload)
   */
  const updateModelDetails = (payload) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_UPDATE_MODEL}`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );

  /**
   * @function  updateModelLinkedProject
   * @description API call for updating model linked project.
   * @param {object} payload - payload data
   * @returns {Promise} AxiosPromise
   * @example  updateModelLinkedProject(payload)
   */

  const updateModelLinkedProject = (payload) =>
    axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_LINK_PROJECT}`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function  fetchDeploymentDetails
   * @description API call for fetching deployment details of a model.
   * @param {number} modelId - selected model id
   * @returns {Promise} AxiosPromise
   * @example  fetchDeploymentDetails(modelId)
   */
  const fetchDeploymentDetails = (modelId) =>
    axios.get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_DEPLOYMENT_DETAILS, {
      params: {
        model_id: modelId,
        project_id: project?.activeProject?.project_id,
      },
    });

  /**
   * @function  startModelDeployment
   * @description API call for starting model deployment.
   * @param {object} payload - payload data
   * @returns {Promise} AxiosPromise
   * @example  startModelDeployment(payload)
   */
  const startModelDeployment = (payload) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_START_DEPLOYMENT
        }`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function  stopModelDeployment
   * @description API call for stop model deployment.
   * @param {object} payload - payload data
   * @returns {Promise} AxiosPromise
   * @example  stopModelDeployment(payload)
   */
  const stopModelDeployment = (payload) =>
    axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_STOP_DEPLOYMENT
        }`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  /**
   * @function  fetchModelFilesDetails
   * @description API call for fetching model files details.
   * @param {number} modelId - selected model id
   * @returns {Promise} AxiosPromise
   * @example  fetchModelFilesDetails(modelId)
   */
  const fetchModelFilesDetails = (modelId, projectId) =>
    axios.get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_MODEL_FILES, {
      params: {
        model_id: modelId,
        project_id: projectId,
      },
    });
  /**
   * @function  fetchDeploymentUsage
   * @description API call for fetching model deployment usage.
   * @param {number} modelId - selected model id
   * @returns {Promise} AxiosPromise
   * @example  fetchDeploymentUsage(modelId)
   */
  const fetchDeploymentUsage = (modelId) =>
    axios.get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_DEPLOYMENT_USAGE, {
      params: {
        model_id: modelId,
        project_id: project?.activeProject?.project_id,
      },
    });

  const documentUpload = (payload) => {
    return axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_DOCUMENT_UPLOAD
        }`,
        payload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  };

  const fetchModelDocumentsList = (
    projectId = null,
    modelId,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    documentType,
    sortField,
    sortOrder
  ) => {
    return axios
      .get(ME_BASE_URL + ApiEndpoints.ME.MM_GET_DOCUMENTS, {
        params: {
          model_id: modelId,
          ...(projectId ? { project_id: projectId } : {}),
          ...(pageSize ? { page_size: pageSize } : {}),
          page_index: pageIndex + 1,
          ...(searchText !== '' ? { search_term: searchText } : {}),
          ...(documentType !== TYPE_ALL ? { document_type: documentType } : {}),
          ...(sortField ? { sort_field: sortField } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setModelDocuments(res.data);
            setTotalModelDocuments(res.headers[totalPageCount]);
            setModelDocumentsPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setModelDocuments([]);
            setTotalModelDocuments(0);
            setModelDocumentsPageCount(0);
          }
          return res;
        },
        (error) => {
          setModelDocuments([]);
          setTotalModelDocuments(0);
          setModelDocumentsPageCount(0);
          throw error;
        }
      );
  };
  const documentUpdate = (payload) => {
    return axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_UPDATE_DOCUMENT
        }`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  };
  const fetchDocumentURL = (documentId) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.MM_DOCUMENT_UPLOAD, {
        params: {
          document_id: documentId,
        },
      })
      .then(
        (res) => {
          if (res.data) {
            return res;
          }
        },
        (error) => {
          throw error;
        }
      );
  const modelLogicalEntry = (payload) => {
    return axios
      .post(
        `${ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_LOGICAL_ENTRY}`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  };

  const fetchModelRelatedUdfs = (
    projectId = null,
    modelId,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    sortField,
    sortOrder
  ) =>
    axios
      .get(ME_BASE_URL + ApiEndpoints.ME.MM_LINK_MODEL_UDF, {
        params: {
          ...(projectId ? { project_id: projectId } : {}),
          model_id: modelId,
          ...(pageSize ? { page_size: pageSize } : {}),
          page_index: pageIndex + 1,
          ...(searchText !== '' ? { search_term: searchText } : {}),
          ...(sortField ? { sort_field: sortField } : {}),
          ...(sortOrder ? { sort_order: sortOrder } : {}),
        },
      })
      .then(
        (res) => {
          if (res.data) {
            setModelRelatedUdfs(res.data);
            setTotalModelRelatedUdfs(res.headers[totalPageCount]);
            setModelRelatedUdfsPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setModelRelatedUdfs([]);
            setTotalModelRelatedUdfs(0);
            setModelRelatedUdfsPageCount(0);
          }
          return res;
        },
        (error) => {
          setModelRelatedUdfs([]);
          setTotalModelRelatedUdfs(0);
          setModelRelatedUdfsPageCount(0);
          throw error;
        }
      );
  const linkModelUdf = (payload) => {
    return axios
      .post(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_LINKING_MODEL_UDF
        }`,
        payload
      )
      .then(
        (resp) => resp,
        (error) => {
          throw error;
        }
      );
  };

  const unLinkModelUdf = (body) =>
    axios
      .delete(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_LINKING_MODEL_UDF
        }`,
        {
          data: body,
        }
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  const deleteModelRelatedDocument = (body) =>
    axios
      .delete(
        `${
          ApiEndpoints.MODULE_BASE_URL.ME + ApiEndpoints.ME.MM_DOCUMENT_UPLOAD
        }`,
        {
          data: body,
        }
      )
      .then(
        (res) => res,
        (error) => {
          throw error;
        }
      );
  const fetchLinkedProjectsList = (
    modelId,
    pageIndex = 0,
    pageSize = 10,
    searchText = '',
    sortField,
    sortOrder
  ) =>
    axios
      .get(
        `${ApiEndpoints.MODULE_BASE_URL.DI}${ApiEndpoints.PROJECT_MANAGEMENT.USER_PROJECTS}`,
        {
          params: {
            model_id: modelId,
            ...(pageSize ? { page_size: pageSize } : {}),
            page_index: pageIndex + 1,
            ...(searchText !== '' ? { search_term: searchText } : {}),
            ...(sortField ? { sort_field: sortField } : {}),
            ...(sortOrder ? { sort_order: sortOrder } : {}),
          },
        }
      )
      .then(
        (res) => {
          if (res.data) {
            setModelLinkedProjects(res.data);
            setTotalModelLinkedProjects(res.headers[totalPageCount]);
            setModelLinkedProjectsPageCount(
              Math.ceil(res.headers[totalPageCount] / pageSize)
            );
          } else {
            setModelLinkedProjects([]);
            setTotalModelLinkedProjects(0);
            setModelLinkedProjectsPageCount(0);
          }
          return res;
        },
        (error) => {
          setModelLinkedProjects([]);
          setTotalModelLinkedProjects(0);
          setModelLinkedProjectsPageCount(0);
          throw error;
        }
      );
  return {
    deleteModelRelatedDocument,
    unLinkModelUdf,
    fetchDocumentURL,
    documentUpdate,
    documentUpload,
    fetchModelDocumentsList,
    modelDocuments,
    modelDocumentsPageCount,
    totalModelDocuments,
    uploadPklFile,
    uploadSas,

    // saved models
    models,
    fetchSavedModelList,
    fetchAllModelListWithPagination,
    modelListWithPagination,
    totalModelListWithPagination,
    modelListPageCountWithPagination,
    setTotalModelListWithPagination,
    setModelListPageCountWithPagination,
    fetchAllModelListWithoutPagination,
    modelListWithoutPagination,
    // deploy model
    deployedTableId,
    deploySavedModel,
    renameModelDisplayName,

    datasetList,
    project,
    fetchParameters,
    parameters,
    // summary stats
    fetchModelSummaryStats,
    modelSummaryStats,
    // related jobs
    fetchModelRelatedJobs,
    modelRelatedJobs,
    modelRelatedJobsPageCount,
    totalModelRelatedJobs,
    // related pipelines
    fetchModelRelatedPipelines,
    modelRelatedPipelines,
    modelRelatedPipelinesPageCount,
    totalModelRelatedPipelines,
    fetchModelRelatedUdfs,
    modelRelatedUdfs,
    totalModelRelatedUdfs,
    modelRelatedUdfsPageCount,
    fetchLinkedProjectsList,
    modelLinkedProjects,
    totalModelLinkedProjects,
    modelLinkedProjectsPageCount,
    // model details
    fetchModelDetails,
    // update model details
    updateModelDetails,
    // link project
    updateModelLinkedProject,
    // model deployment details
    fetchDeploymentDetails,
    // start model deployment
    startModelDeployment,
    // stop model deployment
    stopModelDeployment,
    // model files
    fetchModelFilesDetails,
    // deployment usage
    fetchDeploymentUsage,
    fetchDatasetRowsWithPagination,
    datasetRowsWithPagination,
    setDatasetRowsWithPagination,
    totalDatasetRows,
    datasetRowsPageCount,
    isLoading,

    updateDatasetCells,

    targetColumns,
    datasetColumnDetails,
    deleteModel,
    fetchTargetColumns,
    fetchDatasetColumnsDetails,
    fetchDatasetList,
    fetchDatasetListWithModelId,

    cloneArtifacts,
    modelLogicalEntry,
    fetchAllUDFs,
    linkModelUdf,
    fetchGroups,
    fetchUserRecipients,
    fetchUserProjects,
    fetchProjects,
  };
}
